import { useQuery } from '@tanstack/react-query'
import { Edit, Tag, Trash } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  createPromocao,
  deletePromocao,
  getAllPromocoes,
  getPromocaoById,
  type ICreatePromocao,
  type IPromocao,
  updatePromocao,
} from '@/api/campaigns/promocao'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { PromocaoModal } from './promocaoModal'
import { formatCurrency } from './utils'

export function PromocaoCRUD({ campanhaId }: { campanhaId: number }) {
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPromocao, setCurrentPromocao] = useState<IPromocao | null>(null)

  const { data: promocoes, refetch } = useQuery({
    queryKey: ['promocoes', campanhaId],
    queryFn: () => getAllPromocoes(campanhaId),
  })

  const filteredPromocoes = promocoes?.filter((promocao: IPromocao) =>
    promocao.valor.toString().includes(search),
  )

  const handleCreateOrUpdate = async (data: ICreatePromocao) => {
    try {
      if (currentPromocao) {
        await updatePromocao(currentPromocao.id, data)
        toast.success('Promoção atualizada com sucesso!')
      } else {
        await createPromocao({ ...data, campanha_Id: campanhaId })
        toast.success('Promoção criada com sucesso!')
      }

      refetch()
      setIsModalOpen(false)
      setCurrentPromocao(null)
    } catch (error) {
      toast.error('Erro ao salvar promoção')
    }
  }

  const handleEdit = async (id: number) => {
    const promocao = await getPromocaoById(id)
    setCurrentPromocao(promocao)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deletePromocao(id)
      toast.success('Promoção excluída com sucesso!')
      refetch()
    } catch {
      toast.error('Erro ao excluir promoção')
    }
  }

  return (
    <div className="space-y-4">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-md">
          <Input
            placeholder="Buscar promoções..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="w-full sm:w-auto"
        >
          <Tag className="mr-2 h-4 w-4" />
          Adicionar Promoção
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <div className="min-w-[600px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%] text-center">
                  Quantidade
                </TableHead>
                <TableHead className="w-[40%] text-center">Valor</TableHead>
                <TableHead className="w-[20%] text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPromocoes?.length ? (
                filteredPromocoes.map((promocao: IPromocao) => (
                  <TableRow key={promocao.id}>
                    <TableCell className="text-center">
                      {promocao.quantidade} títulos
                    </TableCell>
                    <TableCell className="text-center">
                      {formatCurrency(promocao.valor)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="grid justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(promocao.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(promocao.id)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    Nenhuma promoção encontrada
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <PromocaoModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleCreateOrUpdate}
        promocao={currentPromocao}
      />
    </div>
  )
}
